'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { AnimatePresence, MotionConfig, motion, type Variants } from 'framer-motion'
import { ChevronLeft, ChevronRight, LogOut } from 'lucide-react'
import { HugeiconsIcon } from '@hugeicons/react'
import { LANDING_GAMES } from '@/lib/landing-games'
import { UserAvatar } from '@/components/ui/user-avatar'
import posthog from 'posthog-js'

// Stable analytics slug for a nav destination: '/app' -> 'my_profile',
// '/leaderboard' -> 'leaderboard'.
function navItemSlug(path: string): string {
  if (path === '/app') return 'my_profile'
  return path.replace(/^\/(app\/)?/, '').replace(/[/-]/g, '_')
}

function trackNavItem(item: string) {
  posthog.capture('mobile_nav_item_clicked', { item, platform: 'web' })
}

export const MOBILE_NAV_MENU_ID = 'mobile-nav-menu'

type HugeIcon = Parameters<typeof HugeiconsIcon>[0]['icon']

export interface MobileAppLink {
  path: string
  label: string
  icon: HugeIcon
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'

const EASE_OUT = [0.22, 1, 0.36, 1] as const

const panelVariants: Variants = {
  closed: { opacity: 0, y: -12, scale: 0.98, transition: { duration: 0.16, ease: 'easeIn' } },
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.28, ease: EASE_OUT, when: 'beforeChildren', staggerChildren: 0.035 },
  },
}

const itemVariants: Variants = {
  closed: { opacity: 0, y: 8 },
  open: { opacity: 1, y: 0, transition: { duration: 0.24, ease: EASE_OUT } },
}

const focusRing =
  'outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-card'

/** Animated hamburger ↔ X. Three bars, transform/opacity only. */
export function MobileMenuToggle({
  open,
  onToggle,
  buttonRef,
}: {
  open: boolean
  onToggle: () => void
  buttonRef: React.RefObject<HTMLButtonElement | null>
}) {
  const bar =
    'absolute left-1/2 top-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-current transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none'
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onToggle}
      aria-label={open ? 'Close menu' : 'Open menu'}
      aria-expanded={open}
      aria-controls={MOBILE_NAV_MENU_ID}
      className="relative -mr-2 h-11 w-11 shrink-0 cursor-pointer rounded-xl text-white transition-colors hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-white/70 md:hidden"
    >
      <span aria-hidden className={`${bar} ${open ? '-translate-y-1/2 rotate-45' : '-translate-y-[7px]'}`} />
      <span aria-hidden className={`${bar} -translate-y-1/2 ${open ? 'scale-x-0 opacity-0' : ''}`} />
      <span aria-hidden className={`${bar} ${open ? '-translate-y-1/2 -rotate-45' : 'translate-y-[5px]'}`} />
    </button>
  )
}

interface MobileMenuSheetProps {
  open: boolean
  onClose: () => void
  /** Distance from the viewport top to the bottom of the header bar. */
  top: number
  pathname: string
  hrefWithLocale: (path: string) => string
  t: Record<string, any>
  session: unknown
  user: { profile_image_url?: string | null; gamertag?: string | null } | null | undefined
  fallbackName?: string
  appLinks: MobileAppLink[]
  onSignOut: () => void
  /** Element the focus trap covers (the whole <nav>, so the toggle stays reachable). */
  trapRef: React.RefObject<HTMLElement | null>
}

export function MobileMenuSheet({
  open,
  onClose,
  top,
  pathname,
  hrefWithLocale,
  t,
  session,
  user,
  fallbackName,
  appLinks,
  onSignOut,
  trapRef,
}: MobileMenuSheetProps) {
  const [gamesOpen, setGamesOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const gamesTriggerRef = useRef<HTMLButtonElement>(null)
  const gamesBackRef = useRef<HTMLButtonElement>(null)

  // Reset the drill-down whenever the sheet closes.
  useEffect(() => {
    if (!open) setGamesOpen(false)
  }, [open])

  // Focus follows the drill-down. Runs after commit, once `inert` has moved panes.
  const pendingFocus = useRef<'back' | 'trigger' | null>(null)
  useEffect(() => {
    const target = pendingFocus.current
    pendingFocus.current = null
    if (target === 'back') gamesBackRef.current?.focus({ preventScroll: true })
    else if (target === 'trigger') gamesTriggerRef.current?.focus({ preventScroll: true })
  }, [gamesOpen])

  const openGames = useCallback(() => {
    pendingFocus.current = 'back'
    setGamesOpen(true)
  }, [])

  const closeGames = useCallback(() => {
    pendingFocus.current = 'trigger'
    setGamesOpen(false)
  }, [])

  // Escape closes (or steps back out of Games first); Tab stays inside the nav.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        if (gamesOpen) {
          closeGames()
        } else {
          onClose()
        }
        return
      }
      if (e.key !== 'Tab' || !trapRef.current) return
      const nodes = Array.from(trapRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null && !el.closest('[inert]'),
      )
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, gamesOpen, onClose, trapRef, closeGames])

  const isActive = (path: string) => {
    const href = hrefWithLocale(path)
    return pathname === href || pathname.startsWith(`${href}/`)
  }

  const gamesActive = isActive('/games')
  const primaryLinks = [
    { path: '/tournaments', label: t.nav?.tournaments || 'Tournaments', live: true },
    { path: '/leaderboard', label: t.nav?.leaderboard || 'Leaderboard', live: false },
  ]
  const profileLink = appLinks.find((l) => l.path === '/app')
  const tileLinks = appLinks.filter((l) => l.path !== '/app')

  const rowBase = `group relative flex min-h-16 w-full items-center gap-4 rounded-2xl px-3 text-left transition-colors hover:bg-white/[0.05] cursor-pointer ${focusRing}`
  const rowLabel = 'font-sans text-[clamp(1.25rem,7vw,1.75rem)] leading-none font-extrabold italic uppercase tracking-tight'

  const ActiveBar = ({ show }: { show: boolean }) =>
    show ? <span aria-hidden className="absolute inset-y-3 left-0 w-0.5 rounded-full bg-primary" /> : null

  return (
    <MotionConfig reducedMotion="user">
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              aria-hidden
              onClick={onClose}
              className="fixed inset-x-0 bottom-0 z-0 bg-black/70 backdrop-blur-sm md:hidden"
              style={{ top: 0 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.24 } }}
              exit={{ opacity: 0, transition: { duration: 0.18 } }}
            />
            <motion.div
              key="panel"
              ref={panelRef}
              id={MOBILE_NAV_MENU_ID}
              role="dialog"
              aria-modal="true"
              aria-label="Menu"
              variants={panelVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="fixed inset-x-3 z-10 flex origin-top flex-col overflow-hidden rounded-3xl border border-white/10 bg-card shadow-2xl shadow-black/70 will-change-transform md:hidden"
              style={{ top: top + 8, height: `calc(100dvh - ${top + 20}px)` }}
            >
              {/* Panes: main list and the Games drill-down slide horizontally (transform only). */}
              <div className="relative flex min-h-0 flex-1 overflow-hidden">
                <div
                  inert={gamesOpen}
                  aria-hidden={gamesOpen}
                  className={`w-full shrink-0 overflow-y-auto overscroll-contain p-2 transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${gamesOpen ? '-translate-x-1/4 opacity-0' : 'translate-x-0 opacity-100'}`}
                >
                  <ul className="flex flex-col">
                    <motion.li variants={itemVariants}>
                      <button
                        ref={gamesTriggerRef}
                        type="button"
                        onClick={() => {
                          trackNavItem('games')
                          openGames()
                        }}
                        aria-expanded={gamesOpen}
                        aria-controls="mobile-nav-games"
                        className={rowBase}
                      >
                        <ActiveBar show={gamesActive} />
                        <span className="w-6 font-space-mono text-[11px] tabular-nums text-white/30">01</span>
                        <span className={`${rowLabel} ${gamesActive ? 'text-white' : 'text-white/85'}`}>
                          {t.nav?.games || 'Games'}
                        </span>
                        <span className="ml-auto flex items-center gap-2 text-white/40 transition-colors group-hover:text-white/70">
                          <span className="font-space-mono text-[11px] tabular-nums">{LANDING_GAMES.length}</span>
                          <ChevronRight size={18} />
                        </span>
                      </button>
                    </motion.li>
                    {primaryLinks.map((link, i) => {
                      const active = isActive(link.path)
                      return (
                        <motion.li key={link.path} variants={itemVariants}>
                          <Link
                            href={hrefWithLocale(link.path)}
                            onClick={() => {
                              trackNavItem(navItemSlug(link.path))
                              onClose()
                            }}
                            aria-current={active ? 'page' : undefined}
                            className={rowBase}
                          >
                            <ActiveBar show={active} />
                            <span className="w-6 font-space-mono text-[11px] tabular-nums text-white/30">
                              {String(i + 2).padStart(2, '0')}
                            </span>
                            <span className={`relative ${rowLabel} ${active ? 'text-white' : 'text-white/85'}`}>
                              {link.label}
                              {link.live && (
                                <span aria-hidden className="absolute -right-3 -top-1 flex h-2 w-2">
                                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75 motion-reduce:animate-none" />
                                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                                </span>
                              )}
                            </span>
                          </Link>
                        </motion.li>
                      )
                    })}
                  </ul>

                  {session ? (
                    <motion.div variants={itemVariants} className="mt-2 border-t border-white/[0.06] px-1 pt-3">
                      {profileLink && (
                        <Link
                          href={hrefWithLocale(profileLink.path)}
                          onClick={() => {
                            trackNavItem('my_profile')
                            onClose()
                          }}
                          aria-current={isActive('/app') && pathname === hrefWithLocale('/app') ? 'page' : undefined}
                          className={`flex min-h-14 items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.03] px-3 py-2 transition-colors hover:bg-white/[0.06] ${focusRing}`}
                        >
                          <UserAvatar
                            url={user?.profile_image_url ?? undefined}
                            alt={user?.gamertag ?? 'You'}
                            size={36}
                            className="ring-1 ring-white/10"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold text-white">
                              {user?.gamertag ?? fallbackName}
                            </span>
                            <span className="block truncate font-space-mono text-[10px] uppercase tracking-widest text-white/40">
                              {profileLink.label}
                            </span>
                          </span>
                          <ChevronRight size={16} className="text-white/40" />
                        </Link>
                      )}
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {tileLinks.map(({ path, label, icon }) => {
                          const active = isActive(path)
                          return (
                            <Link
                              key={path}
                              href={hrefWithLocale(path)}
                              onClick={() => {
                                trackNavItem(navItemSlug(path))
                                onClose()
                              }}
                              aria-current={active ? 'page' : undefined}
                              className={`flex min-h-12 min-w-0 items-center gap-2.5 rounded-xl border px-3 text-sm font-medium transition-colors hover:bg-white/[0.07] hover:text-white ${active ? 'border-white/15 bg-white/[0.06] text-white' : 'border-white/[0.06] bg-white/[0.02] text-white/75'} ${focusRing}`}
                            >
                              <HugeiconsIcon
                                icon={icon}
                                size={17}
                                strokeWidth={2}
                                className={active ? 'text-primary' : 'text-white/50'}
                              />
                              <span className="truncate">{label}</span>
                            </Link>
                          )
                        })}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          trackNavItem('sign_out')
                          onClose()
                          onSignOut()
                        }}
                        className={`mt-2 flex min-h-11 w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 text-sm font-medium text-white/50 transition-colors hover:bg-white/[0.05] hover:text-white ${focusRing}`}
                      >
                        <LogOut size={16} />
                        {t.nav?.signOut || 'Sign Out'}
                      </button>
                    </motion.div>
                  ) : null}
                </div>

                {/* Games drill-down */}
                <div
                  id="mobile-nav-games"
                  inert={!gamesOpen}
                  aria-hidden={!gamesOpen}
                  className={`absolute inset-0 flex flex-col transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none ${gamesOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
                >
                  <div className="flex items-center gap-3 border-b border-white/[0.06] px-2 py-2">
                    <button
                      ref={gamesBackRef}
                      type="button"
                      onClick={closeGames}
                      aria-label="Back"
                      className={`flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl text-white/70 transition-colors hover:bg-white/[0.06] hover:text-white ${focusRing}`}
                    >
                      <ChevronLeft size={20} />
                    </button>
                    <span className="font-space-mono text-xs uppercase tracking-[0.2em] text-white/50">
                      {t.dropdown?.allGames || 'All Games'}
                    </span>
                    <span className="ml-auto pr-3 font-space-mono text-xs tabular-nums text-white/30">
                      {LANDING_GAMES.length}
                    </span>
                  </div>
                  <ul className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2">
                    {LANDING_GAMES.map((game) => {
                      const active = isActive(`/games/${game.slug}`)
                      return (
                        <li key={game.id}>
                          <Link
                            href={hrefWithLocale(`/games/${game.slug}`)}
                            onClick={() => {
                              trackNavItem(`game_${game.slug.replace(/-/g, '_')}`)
                              onClose()
                            }}
                            aria-current={active ? 'page' : undefined}
                            className={`relative flex min-h-12 items-center gap-3 rounded-xl px-2.5 py-1.5 transition-colors hover:bg-white/[0.05] ${active ? 'bg-white/[0.05]' : ''} ${focusRing}`}
                          >
                            <ActiveBar show={active} />
                            <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg bg-white/[0.06] ring-1 ring-white/10">
                              <Image
                                src={game.image || '/placeholder.svg'}
                                alt=""
                                fill
                                sizes="36px"
                                className="object-cover"
                              />
                            </span>
                            <span className="truncate text-[15px] font-medium text-white">{game.display_name}</span>
                          </Link>
                        </li>
                      )
                    })}
                    <li className="mt-1 border-t border-white/[0.06] pt-1">
                      <Link
                        href={hrefWithLocale('/games')}
                        onClick={() => {
                          trackNavItem('all_games')
                          onClose()
                        }}
                        className={`flex min-h-12 items-center justify-between rounded-xl px-2.5 font-space-mono text-xs uppercase tracking-wider text-white/50 transition-colors hover:bg-white/[0.05] hover:text-white ${focusRing}`}
                      >
                        <span>{t.dropdown?.viewAll || 'View All Games'}</span>
                        <span aria-hidden>→</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Pinned CTAs */}
              <motion.div
                variants={itemVariants}
                className="flex flex-col gap-2 border-t border-white/[0.06] bg-card p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              >
                {!session && (
                  <Link
                    href={hrefWithLocale('/login')}
                    onClick={() => {
                      trackNavItem('sign_in')
                      onClose()
                    }}
                    className={`flex h-12 items-center justify-center rounded-xl bg-primary text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-primary/90 ${focusRing}`}
                  >
                    {t.nav?.signIn || 'Sign In'}
                  </Link>
                )}
                <Link
                  href={hrefWithLocale('/download')}
                  onClick={() => {
                    trackNavItem('download_app')
                    onClose()
                  }}
                  className={
                    session
                      ? `flex h-12 items-center justify-center rounded-xl bg-primary text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-primary/90 ${focusRing}`
                      : `flex h-12 items-center justify-center rounded-xl border border-white/15 bg-white/[0.03] text-sm font-bold uppercase tracking-wide text-white/90 transition-colors hover:bg-white/10 hover:text-white ${focusRing}`
                  }
                >
                  {t.nav?.downloadApp || 'Download App'}
                </Link>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </MotionConfig>
  )
}
